<!-- app-subdomain-creators\src\Components\CreatorCreateNewTagModal.vue -->

<template>
  <TransitionRoot as="template" :show="show">
    <Dialog class="relative z-50" @close="emitClose">
      <!-- Overlay -->
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm transition-opacity" />
      </TransitionChild>

      <!-- Modal Container -->
      <div class="fixed top-[23%] inset-x-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden rounded-[16px] bg-zinc-100 px-5 pb-5 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
            >
              <!-- Close button -->
              <button
                type="button"
                class="absolute top-3 right-3 rounded-full bg-zinc-100 text-gray-400 p-1 hover:bg-gray-200 hover:text-gray-500 dark:bg-zinc-900 dark:text-zinc-500 dark:hover:bg-zinc-800 dark:hover:text-gray-300"
                @click="emitClose"
              >
                <XMarkIcon class="h-6 w-6" />
              </button>

              <!-- Title -->
              <DialogTitle
                as="h3"
                class="text-lg font-semibold leading-6 text-gray-900"
              >
                Create Tag
              </DialogTitle>

              <p class="text-sm text-zinc-500 mt-1">
                Create a reusable tag you can apply to members (e.g. "Hot Lead", "VIP", "Trial").
              </p>

              <!-- INPUT -->
              <div class="mt-6">
                <label class="text-sm text-zinc-600 font-medium">Tag name</label>
                <input
                  v-model="tagName"
                  class="mt-2 w-full rounded-lg border border-zinc-300 p-2 bg-white focus:ring-[#2D8CFF] focus:border-[#2D8CFF]"
                  maxlength="100"
                  placeholder="Hot Lead"
                  @keyup.enter.prevent="emitCreate"
                />
                <p class="mt-1 text-xs text-zinc-400">
                  Tags are visible only to you. Enter at least 2 characters. You can use the English alphabet, numbers, and underscores ("_"). Max 100 characters.
                </p>
              </div>

              <!-- BUTTONS -->
              <div class="mt-8 flex justify-end gap-3">
                <button
                  @click="emitClose"
                  class="px-4 py-2 text-sm rounded-full bg-zinc-200 text-zinc-700 hover:bg-zinc-300"
                >
                  Cancel
                </button>

                <button
                  @click="emitCreate"
                  class="px-4 py-2 text-sm rounded-full bg-[#2D8CFF] text-white hover:bg-[#1b6ad1] disabled:opacity-60 disabled:cursor-not-allowed"
                  :disabled="!tagNameTrimmed.length"
                >
                  Save
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'

const emit = defineEmits(['close', 'create'])

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

const tagName = ref('')

const tagNameTrimmed = computed(() => (tagName.value || '').trim())

watch(
  () => props.show,
  (val) => {
    if (val) {
      tagName.value = ''
    }
  }
)

function emitClose() {
  emit('close')
}

function emitCreate() {
  const name = tagNameTrimmed.value
  if (!name) return
  emit('create', name)
}
</script>
